<template>
    <div class="fx-select">
        <div class="fx-select-native" v-if="isMobileDevice && !disableNative">
            <span v-if="!defaultOption && options.length === 1" class="fx-selection">
                {{ options[0].label }} {{ options[0].all_in_price }}
            </span>
            <div v-else>
                <select
                    :aria-label="ariaLabel"
                    :disabled="disabled"
                    :required="required"
                    :value="selection ? selection.value : null"
                    @change="onNativeSelectChange"
                >
                    <option v-if="defaultOption" :value="defaultOption.value">
                        {{ defaultOption.label }}
                    </option>
                    <option v-for="option in options" :key="option.value" :value="option.value">
                        {{ option.label }}
                    </option>
                </select>
                <span class="fx-select-native__all-in-price" v-if="selection.all_in_price">
                    {{ selection.all_in_price }}
                </span>
            </div>
        </div>
        <div
            :aria-label="ariaLabel"
            :class="{ disabled, selectable: isSelectable }"
            class="fx-select-custom"
            v-if="!isMobileDevice || disableNative"
            v-on-click-outside.static="close"
        >
            <div
                :class="{ open: isDropdownOpen }"
                class="fx-select-input"
                data-index="0"
                ref="selectInput"
                tabindex="0"
                @click="onCustomSelectClick"
                @keydown.enter="onCustomSelectClick"
                @keydown.down.prevent="setFocus(1)"
            >
                <span v-if="selection" class="fx-selection">
                    {{ selection.label }} {{ selection.all_in_price }}
                </span>
                <span v-else class="fx-placeholder">{{ placeholder }}</span>
                <slot name="caret" v-if="isSelectable && (!value || !defaultOption)">
                    <span class="icon-caret-down"></span>
                </slot>
                <span
                    aria-label="clear selection"
                    class="icon-cancel"
                    tabindex="0"
                    v-else-if="isSelectable && value"
                    @click.stop="clear"
                    @keyup.enter.stop="clear"
                ></span>
            </div>
            <div
                class="fx-select-dropdown"
                ref="dropdown"
                v-show="isDropdownOpen && isSelectable"
                :class="{ left: align === 'left', right: align === 'right' }"
                :style="{ 'max-height': maxDropDownHeight + 'px' }"
            >
                <ul ref="selectOptions" class="fx-list">
                    <li
                        :class="{ selected: defaultOption === selection }"
                        data-index="1"
                        tabindex="0"
                        v-if="defaultOption"
                        @click="
                            select(defaultOption.value)
                            setFocus(0)
                        "
                        @keydown.enter="
                            select(defaultOption.value)
                            setFocus(0)
                        "
                        @keydown.up.prevent="setFocus(0)"
                        @keydown.down.prevent="setFocus(2)"
                    >
                        <span class="icon-checkmark" v-show="defaultOption === selection"></span>
                        <span class="fx-option-label">
                            {{ defaultOption.label }}
                        </span>
                    </li>
                    <li
                        :class="{ selected: option === selection }"
                        :data-index="defaultOption ? index + 2 : index + 1"
                        :key="option.value"
                        tabindex="0"
                        v-for="(option, index) in options"
                        @click="
                            select(option.value)
                            setFocus(0)
                        "
                        @keydown.enter="
                            select(option.value)
                            setFocus(0)
                        "
                        @keydown.up.prevent="setFocus(defaultOption ? index + 1 : index)"
                        @keydown.down.prevent="setFocus(defaultOption ? index + 3 : index + 2)"
                    >
                        <span class="icon-checkmark" v-show="option === selection"></span>
                        <span class="fx-option-label">{{ option.label }}</span>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
import { mixin as onClickOutside } from 'vue-on-click-outside'
import MobileDetect from 'mobile-detect'

export default {
    props: {
        value: {
            required: true
        },
        options: {
            type: Array, // Array<Option> -> [{ value:Any, label:String }]
            required: true
        },
        defaultOption: {
            type: Object, // Option -> { value:Any, label:String }
            default: () => ({
                value: null,
                label: 'Select'
            })
        },
        align: {
            // left/right
            type: String,
            default: 'left'
        },
        placeholder: String,
        required: Boolean,
        disabled: Boolean,
        ariaLabel: String,
        disableNative: Boolean
    },
    data () {
        return {
            isDropdownOpen: false,
            isMobileDevice: Boolean(new MobileDetect(window.navigator.userAgent).mobile()),
            maxDropDownHeight: 256
        }
    },
    computed: {
        selection () {
            // Rerender template to prevent race condition when selection changes
            this.$forceUpdate()
            let selectedOption = this.options.find((option) => option.value === this.value)
            if (!selectedOption && this.defaultOption) {
                selectedOption = this.defaultOption
            }
            return selectedOption
        },
        isSelectable () {
            return !this.disabled && this.options.length > 1
        }
    },
    methods: {
        select (selectedValue) {
            this.$emit('input', selectedValue)
            this.close()
        },
        clear () {
            this.$emit('input', null)
            this.close()
        },
        open () {
            if (this.options.length > 1) {
                this.isDropdownOpen = true
                this.$nextTick(() => {
                    this.updateMaxHeight()
                })
            }
        },
        close () {
            if (this.isDropdownOpen) {
                this.isDropdownOpen = false
            }
        },
        toggleOpen () {
            ;(this.isDropdownOpen ? this.close : this.open)()
        },
        setFocus (index) {
            if (index === 0) {
                this.$refs.selectInput.focus()
            } else {
                const option = this.$refs.selectOptions.querySelector(`[data-index="${index}"]`)
                if (option) {
                    option.focus()
                }
            }
        },
        onCustomSelectClick () {
            if (!this.disabled && this.options.length > 1) {
                this.toggleOpen()
            }
        },
        onNativeSelectChange (event) {
            const optionIndex = event.srcElement.selectedIndex
            let selection = null
            if (this.defaultOption) {
                selection = optionIndex === 0 ? this.defaultOption.value : this.options[optionIndex - 1].value
            } else {
                selection = this.options[optionIndex].value
            }
            this.select(selection)
        },
        updateMaxHeight () {
            const dropdownElem = this.$refs.dropdown
            let maxHeight = 256
            if (dropdownElem) {
                const dropdownRect = dropdownElem.getBoundingClientRect()
                if (dropdownRect.top > 0 && dropdownRect.top < window.innerHeight) {
                    const margin = 6
                    maxHeight = Math.min(maxHeight, window.innerHeight - dropdownRect.top - margin)
                }
            }
            this.maxDropDownHeight = maxHeight
        }
    },
    mixins: [onClickOutside]
}
</script>

<style lang="postcss" scoped>
@import 'theme';

.fx-select {
    display: inline-block;
    max-width: 100%;
}

.fx-select-native {
    width: auto;
    & > select {
        max-width: 100%;
    }
    & .fx-select-native__all-in-price {
        align-items: center;
        display: flex;
        margin-left: 7px;
        width: 200px;
    }
}

.fx-select-custom {
    cursor: default;
    position: static;
    @media (--mq-large-min) {
        position: relative;
    }
    &.disabled {
        & > .fx-select-input {
            border-color: var(--c-interact-disabled);
            & > .fx-selection {
                color: var(--c-interact-disabled);
            }
            & > .icon-caret-down {
                color: var(--c-interact-disabled);
            }
        }
    }
    &.selectable {
        cursor: pointer;
        & > .fx-select-input {
            &:focus {
                border-color: var(--c-gray-1);
            }
            & > .fx-selection {
                margin-right: 6px;
            }
            & > input {
                cursor: pointer;
            }
        }
    }
}

.fx-select-input {
    align-items: center;
    border-bottom: solid 2px transparent;
    color: var(--c-gray-1);
    display: inline-flex;
    max-width: 100%;
    outline: none;
    padding: 4px 0;
    transition: border-color 100ms ease-in-out;
    & > .fx-placeholder {
        color: var(--c-gray-4);
    }
    & > .icon-caret-down,
    & > .icon-cancel {
        font-size: var(--fs-smaller);
    }
}

.fx-selection {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.fx-select-dropdown {
    background-color: white;
    border-radius: var(--d-border-radius);
    box-shadow: var(--sh-shallow);
    overflow-y: auto;
    position: absolute;
    z-index: var(--layer-1);
    width: 300px;
    &.left {
        left: 50vw;
        margin-left: -150px;
    }
    &.right {
        right: 50vw;
        margin-right: calc(-150px - var(--site-padding));
    }
    @media (--mq-large-min) {
        width: 200px;
        &.left {
            left: 0;
            margin-left: 0;
        }
        &.right {
            right: 0;
            margin-right: 0;
        }
    }
    & > ul {
        & > li {
            align-items: center;
            border-bottom: solid 1px var(--c-gray-9);
            cursor: pointer;
            display: flex;
            outline: none;
            padding: 8px;
            &:last-child {
                border-bottom: none;
            }
            &:hover,
            &:focus {
                background-color: var(--c-interact-hover);
                & > .fx-option-label {
                    text-decoration: underline;
                }
            }
            &.selected {
                color: var(--c-interact-active);
            }
            & > .icon-checkmark {
                display: inline-flex;
                margin-right: 8px;
            }
        }
    }
}
</style>
